<template>
  <div class="kpi-sparkline-wrap">
    <div class="metric-label">売上金額推移（直近7日）</div>
    <div class="sparkline-chart-area">
      <svg ref="svg" viewBox="0 0 200 48" preserveAspectRatio="none" class="sparkline-svg">
        <path :d="areaPath" :fill="fillColor" />
        <polyline
          :points="linePoints"
          :stroke="lineColor"
          fill="none"
          stroke-width="2"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
        <!-- invisible hit areas for hover -->
        <circle
          v-for="(pt, i) in pts"
          :key="'h' + i"
          :cx="pt.x"
          :cy="pt.y"
          r="10"
          fill="transparent"
          @mouseenter="onHover(i)"
          @mouseleave="hoveredIdx = -1"
        />
        <!-- visible dots -->
        <circle
          v-for="(pt, i) in pts"
          :key="i"
          :cx="pt.x"
          :cy="pt.y"
          :r="i === peakIdx || i === hoveredIdx ? 4 : 2.5"
          :fill="i === peakIdx ? '#FF9800' : lineColor"
          style="pointer-events: none"
        />
      </svg>
    </div>
    <div class="sparkline-x-labels-full">
      <span
        v-for="(pt, i) in pts"
        :key="i"
        :style="{ left: (pt.x / 200 * 100) + '%' }"
        :class="{ 'is-peak': i === peakIdx }"
      >{{ dateLabels[i] }}</span>
    </div>
    <!-- position: fixed → not clipped by any ancestor overflow:hidden -->
    <div
      v-if="hoveredIdx >= 0"
      class="kpi-sparkline-tooltip"
      :style="{ left: tooltipClientX + 'px', top: tooltipClientY + 'px' }"
    >
      <div class="tt-date">{{ tooltipDate }}</div>
      <div class="tt-val">金額 {{ tooltipValue }}</div>
      <div v-if="tooltipOrders" class="tt-extra">件数 {{ tooltipOrders }}</div>
      <div v-if="tooltipSales" class="tt-extra">個数 {{ tooltipSales }}</div>
      <div v-if="tooltipReturns" class="tt-extra">返品 {{ tooltipReturns }}</div>
    </div>
  </div>
</template>

<script>
import Util from '@/common/utility'

export default {
  props: {
    data: { type: Array, default: () => [] },
    ordersData: { type: Array, default: () => [] },
    salesData: { type: Array, default: () => [] },
    returnsData: { type: Array, default: () => [] }
  },
  data () {
    return {
      hoveredIdx: -1,
      tooltipClientX: 0,
      tooltipClientY: 0
    }
  },
  computed: {
    reversed () {
      return [...this.data].reverse()
    },
    reversedOrders () {
      return [...this.ordersData].reverse()
    },
    reversedSales () {
      return [...this.salesData].reverse()
    },
    reversedReturns () {
      return [...this.returnsData].reverse()
    },
    values () {
      return this.reversed.map(d => d.value || 0)
    },
    unit () {
      return (this.data[0] && this.data[0].unit) || ''
    },
    pts () {
      const vals = this.values
      if (vals.length < 2) return []
      const W = 200
      const H = 48
      const px = 10
      const py = 6
      const min = Math.min(...vals)
      const max = Math.max(...vals)
      const range = max - min || 1
      return vals.map((v, i) => ({
        x: px + (i / (vals.length - 1)) * (W - 2 * px),
        y: H - py - ((v - min) / range) * (H - 2 * py)
      }))
    },
    peakIdx () {
      if (!this.values.length) return -1
      return this.values.indexOf(Math.max(...this.values))
    },
    linePoints () {
      return this.pts.map(p => `${p.x},${p.y}`).join(' ')
    },
    areaPath () {
      if (!this.pts.length) return ''
      const H = 48
      const coords = this.pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
      const last = this.pts[this.pts.length - 1]
      const first = this.pts[0]
      return `${coords} L${last.x},${H} L${first.x},${H} Z`
    },
    trend () {
      const vals = this.values
      if (vals.length < 7) return 'flat'
      const front = (vals[0] + vals[1] + vals[2]) / 3
      const back = (vals[4] + vals[5] + vals[6]) / 3
      if (front === 0) return back > 0 ? 'up' : 'flat'
      if (back >= front * 1.05) return 'up'
      if (back <= front * 0.95) return 'down'
      return 'flat'
    },
    lineColor () {
      return { up: '#43a047', down: '#ef4444', flat: '#9ca3af' }[this.trend]
    },
    fillColor () {
      return {
        up: 'rgba(67,160,71,0.1)',
        down: 'rgba(239,68,68,0.1)',
        flat: 'rgba(156,163,175,0.1)'
      }[this.trend]
    },
    dateLabels () {
      return this.reversed.map(d => this.fmtDate(d.orderDate))
    },
    tooltipDate () {
      const r = this.reversed
      if (this.hoveredIdx < 0 || !r[this.hoveredIdx]) return ''
      return this.fmtDate(r[this.hoveredIdx].orderDate)
    },
    tooltipValue () {
      if (this.hoveredIdx < 0) return ''
      const v = this.values[this.hoveredIdx]
      const u = (this.reversed[this.hoveredIdx] && this.reversed[this.hoveredIdx].unit) || this.unit
      if (u === '円') return '￥' + Util.formatMoney(v)
      return v + u
    },
    tooltipOrders () {
      const d = this.reversedOrders[this.hoveredIdx]
      return d ? `${Util.formatMoney(d.value)}${d.unit}` : ''
    },
    tooltipSales () {
      const d = this.reversedSales[this.hoveredIdx]
      return d ? `${Util.formatMoney(d.value)}${d.unit}` : ''
    },
    tooltipReturns () {
      const d = this.reversedReturns[this.hoveredIdx]
      return d ? `${Util.formatMoney(d.value)}${d.unit}` : ''
    }
  },
  methods: {
    onHover (i) {
      this.hoveredIdx = i
      const svg = this.$refs.svg
      if (!svg || !this.pts[i]) return
      const rect = svg.getBoundingClientRect()
      const pt = this.pts[i]
      // scale from SVG viewBox coords to actual screen pixels
      const scaleX = rect.width / 200
      const scaleY = rect.height / 48
      this.tooltipClientX = rect.left + pt.x * scaleX
      this.tooltipClientY = rect.top + pt.y * scaleY
    },
    fmtDate (dateStr) {
      if (!dateStr) return ''
      const parts = dateStr.split('/')
      if (parts.length === 3) return `${parseInt(parts[1])}/${parseInt(parts[2])}`
      return dateStr
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/view/home/components/KpiSparkline.vue